import { Component, inject, OnInit, OnDestroy, signal, Input } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MessageService } from 'primeng/api'
import { ToastModule } from 'primeng/toast'
import { PaymentIntentResponse, PaymentService } from '../../services/payment.service'
import { TranslatePipe } from '../../core/pipes/translate.pipe'

declare const Stripe: any

@Component({
  selector: 'app-payment',
  standalone: true,
  providers: [MessageService],
  imports: [CommonModule, ToastModule, TranslatePipe],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit, OnDestroy {
  @Input({ required: true }) consultationId!: string
  /** Nombre del paciente para mostrar en el formulario */
  @Input() patientName = ''
  /** Callback cuando el pago se completa correctamente */
  @Input() onSuccess?: () => void

  private paymentSvc = inject(PaymentService)
  private toast = inject(MessageService)

  // ── State ──────────────────────────────────────────────────────────────────
  loading = signal(true)
  processing = signal(false)
  paid = signal(false)
  error = signal<string | null>(null)
  intentData = signal<PaymentIntentResponse | null>(null)

  private stripe: any = null
  private elements: any = null
  private paymentElement: any = null

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.initPayment()
  }

  ngOnDestroy(): void {
    this.paymentElement?.destroy()
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  private initPayment(): void {
    this.paymentSvc.createPaymentIntent(this.consultationId).subscribe({
      next: async (data) => {
        this.intentData.set(data)
        await this.loadStripe(data)
        this.loading.set(false)
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'No se pudo inicializar el pago')
        this.loading.set(false)
      },
    })
  }

  private async loadStripe(data: PaymentIntentResponse): Promise<void> {
    // Cargar Stripe.js dinámicamente si no está ya cargado
    if (typeof Stripe === 'undefined') {
      await this.loadStripeScript()
    }

    this.stripe = Stripe(data.publicKey)
    this.elements = this.stripe.elements({
      clientSecret: data.clientSecret,
      appearance: this.stripeAppearance(),
      locale: 'es',
    })

    this.paymentElement = this.elements.create('payment', {
      layout: { type: 'tabs', defaultCollapsed: false },
      fields: { billingDetails: 'auto' },
    })

    // Montar en el contenedor DOM
    setTimeout(() => {
      this.paymentElement.mount('#stripe-payment-element')
      this.paymentElement.on('ready', () => this.loading.set(false))
    }, 0)
  }

  private loadStripeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.getElementById('stripe-js')
      if (existing) {
        resolve()
        return
      }
      const script = document.createElement('script')
      script.id = 'stripe-js'
      script.src = 'https://js.stripe.com/v3/'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('No se pudo cargar Stripe.js'))
      document.head.appendChild(script)
    })
  }

  // ── Confirmar pago ─────────────────────────────────────────────────────────

  async onSubmit(): Promise<void> {
    if (this.processing() || this.paid()) return
    this.processing.set(true)
    this.error.set(null)

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: `${window.location.origin}/pago-completado`,
          payment_method_data: {
            billing_details: { name: this.patientName || 'Paciente' },
          },
        },
        redirect: 'if_required', // evitar redirección en tarjetas sin 3DS
      })

      if (error) {
        // Error de validación de la tarjeta — mostramos al usuario
        this.error.set(error.message)
        this.processing.set(false)
      } else {
        // Pago completado — el webhook de Stripe actualizará la BD
        this.paid.set(true)
        this.processing.set(false)
        this.toast.add({
          severity: 'success',
          summary: '',
          detail: '¡Pago completado correctamente!',
          life: 5000,
        })
        this.onSuccess?.()
      }
    } catch (err: any) {
      console.error('Error al procesar el pago:', err)
      this.error.set('Ha ocurrido un error al procesar el pago')
      this.processing.set(false)
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  get formattedAmount(): string {
    const data = this.intentData()
    if (!data) return ''
    return PaymentService.formatAmount(data.amount, data.currency)
  }

  /** Tema de Stripe Elements ajustado al branding activo */
  private stripeAppearance() {
    const primaryColor = this.primaryBrandColor()

    return {
      theme: 'stripe',
      variables: {
        colorPrimary: primaryColor,
        colorBackground: '#ffffff',
        colorText: '#333333',
        colorDanger: '#dc2626',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
        fontSizeBase: '14px',
      },
      rules: {
        '.Input': {
          border: '1.5px solid #e0e8f5',
          boxShadow: 'none',
          padding: '10px 12px',
        },
        '.Input:focus': {
          border: `1.5px solid ${primaryColor}`,
          boxShadow: `0 0 0 3px ${this.withAlpha(primaryColor, '14')}`,
        },
        '.Label': {
          fontWeight: '500',
          fontSize: '13px',
          color: '#555555',
          marginBottom: '6px',
        },
        '.Error': { color: '#dc2626', fontSize: '12px' },
      },
    }
  }

  private primaryBrandColor(): string {
    if (typeof document === 'undefined') return '#d50475'
    return document.documentElement.style.getPropertyValue('--color-principal').trim() || '#d50475'
  }

  private withAlpha(color: string, alpha: string): string {
    const normalized = color.trim().replace(/^#/, '')
    if (!/^(?:[\da-f]{3}|[\da-f]{6})$/i.test(normalized)) return 'transparent'
    const hex =
      normalized.length === 3
        ? normalized
            .split('')
            .map((part) => `${part}${part}`)
            .join('')
        : normalized
    return `#${hex}${alpha}`
  }
}
